import React from 'react';
import { ArrowUp, ArrowDown } from 'lucide-react';

const TEAL = '#3FCED2';
const DOWN_COLOR = '#f97316'; // orange für Downvote-Highlight

interface VoteButtonsProps {
  score: number;
  /** Aktueller Vote des Users: 1, -1 oder 0/undefined */
  userVote?: number | null;
  /**
   * Wird mit dem GEWÜNSCHTEN neuen Vote aufgerufen:
   * 1 / -1 = Vote setzen bzw. ändern, 0 = aktiven Vote entfernen
   * (erneuter Klick auf den aktiven Pfeil).
   */
  onVote: (newVote: 1 | -1 | 0) => void;
  size?: 'sm' | 'md';
  vertical?: boolean;
  disabled?: boolean;
}

/**
 * Up-/Downvote-Buttons mit Highlighting des eigenen Votes
 * (Port der Vote-Pfeile aus forum_topic_view.dart der Flutter-App).
 */
const VoteButtons: React.FC<VoteButtonsProps> = ({
  score,
  userVote,
  onVote,
  size = 'md',
  vertical = false,
  disabled = false,
}) => {
  const iconSize = size === 'sm' ? 16 : 20;
  const vote = userVote ?? 0;

  const handleUp = () => {
    if (disabled) return;
    onVote(vote === 1 ? 0 : 1);
  };

  const handleDown = () => {
    if (disabled) return;
    onVote(vote === -1 ? 0 : -1);
  };

  return (
    <div
      className={`flex items-center gap-1 ${
        vertical ? 'flex-col' : 'flex-row'
      }`}
    >
      <button
        type="button"
        onClick={handleUp}
        disabled={disabled}
        aria-label="Upvote"
        title={vote === 1 ? 'Upvote entfernen' : 'Upvote'}
        className={`rounded-lg p-1.5 transition-colors ${
          disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-zinc-800'
        }`}
        style={vote === 1 ? { backgroundColor: `${TEAL}26` } : undefined}
      >
        <ArrowUp
          size={iconSize}
          style={{ color: vote === 1 ? TEAL : '#9ca3af' }}
        />
      </button>

      <span
        className={`font-bold tabular-nums ${
          size === 'sm' ? 'text-xs' : 'text-sm'
        }`}
        style={{
          color: vote === 1 ? TEAL : vote === -1 ? DOWN_COLOR : '#fff',
          minWidth: size === 'sm' ? 16 : 20,
          textAlign: 'center',
        }}
      >
        {score}
      </span>

      <button
        type="button"
        onClick={handleDown}
        disabled={disabled}
        aria-label="Downvote"
        title={vote === -1 ? 'Downvote entfernen' : 'Downvote'}
        className={`rounded-lg p-1.5 transition-colors ${
          disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-zinc-800'
        }`}
        style={vote === -1 ? { backgroundColor: `${DOWN_COLOR}26` } : undefined}
      >
        <ArrowDown
          size={iconSize}
          style={{ color: vote === -1 ? DOWN_COLOR : '#9ca3af' }}
        />
      </button>
    </div>
  );
};

export default VoteButtons;
